<template>
  <div class="ac-card-content">
    <div class="ac-header-container">
      <div class="ac-header">
        New Payment Method
      </div>
      <button
        type="button"
        class="ac-close-button"
        @click="toggleActionModal(false)"
      >
        <img
          src="@shared/assets/img/general/x-black.png"
          alt="x"
          class="ac-close"
        >
      </button>
    </div>
    <div class="ac-body-container">
      <button
        v-if="showCreditCard"
        type="button"
        class="payment-button"
        @click="toggleActionModal(true, 'credit-card')"
      >
        Credit Card
      </button>
      <button
        type="button"
        class="payment-button"
        @click="handleACH"
      >
        Direct Debit/ACH
      </button>
      <button
        type="button"
        class="payment-button cardless"
        @click="handleGoCardless"
      >
        <img
          src="@shared/assets/img/payment/go-cardless.png"
          alt="GoCardless"
          class="cardless-logo"
        >
      </button>
    </div>
    <div class="ac-footer-container">
      <button
        type="button"
        class="ac-form-cancel-button"
        @click="toggleActionModal(false)"
      >
        Cancel
      </button>
    </div>
  </div>
</template>

<script>
import apiClient from '@shared/lib/general/apiClient';

export default {
  name: 'NewPaymentMethod',
  props: {
    corpId: Number,
    firstName: String,
    lastName: String,
    toEmail: String,
    changes: Function,
    toggleActionModal: Function,
    onSubmit: Function,
    showCreditCard: Boolean,
  },
  methods: {
    async getACHSetupIntent() {
      const { data } = await apiClient({
        method: 'GET',
        url: `/stripe/corp/${this.corpId}/payment-intent/ach`,
      });
      if (data.client_secret) {
        return data.client_secret;
      }
      throw new Error('Client secret was not returned');
    },
    async getGoCardlessBillingUrl() {
      const { data } = await apiClient({
        method: 'GET',
        url: `/gocardless/billing-flow?redirectUri=${window.location}`,
      });
      return data;
    },
    async handleACH() {
      this.changes([{
        field: 'processingDirectDebit',
        value: true,
      }]);
      const stripe = Stripe(process.env.STRIPE_KEY);
      const prod = process.env.CURRENT_ENV === 'production';
      const clientSecret = await this.getACHSetupIntent();
      const { setupIntent, error } = await stripe.collectBankAccountForSetup({
        clientSecret,
        params: {
          payment_method_type: 'us_bank_account',
          payment_method_data: {
            billing_details: {
              name: `${this.firstName} ${this.lastName}`,
              email: prod ? this.toEmail.trim() : 'testing+test_email@smartac.com',
            },
          },
        },
        expand: ['payment_method'],
      });
      if (error) {
        console.error(error.message);
        this.$store.dispatch('showAlertToast', {
          message: 'There was an unexpected error creating payment method. Please try again or contact support',
        });
      } else if (setupIntent.status === 'requires_confirmation') {
        this.changes([{
          field: 'setupIntent',
          value: setupIntent,
        }]);
        this.toggleActionModal(true, 'confirm-mandate');
      }
      this.changes([{
        field: 'processingDirectDebit',
        value: false,
      }]);
    },
    async handleGoCardless() {
      this.changes([{
        field: 'processingDirectDebit',
        value: true,
      }]);
      try {
        const billingFlow = await this.getGoCardlessBillingUrl();
        const prod = process.env.CURRENT_ENV === 'production';
        const handler = GoCardlessDropin.create({
          billingRequestFlowID: billingFlow.id,
          environment: prod ? 'live' : 'sandbox',
          onSuccess: this.handleCardlessSuccess,
          onExit: this.handleCardlessFail,
        });
        handler.open();
      } catch (err) {
        console.error(err);
        this.changes([{
          field: 'processingDirectDebit',
          value: false,
        }]);
        this.$store.dispatch('showAlertToast', {
          header: 'There was an unexpected error initializing GoCardless.',
          message: 'Please try again or contact support',
        });
      }
    },
    async handleCardlessSuccess(req) {
      const {
        links: {
          mandate,
        },
      } = req.mandate_request;
      try {
        this.changes([{
          field: 'source',
          value: mandate,
        }, {
          field: 'paymentMethod',
          value: 'GoCardless',
        }]);
        await this.onSubmit();
        this.changes([{
          field: 'processingDirectDebit',
          value: false,
        }]);
      } catch (err) {
        console.error(err);
      }
    },
    handleCardlessFail(err) {
      console.error(err);
      this.changes([{
        field: 'processingDirectDebit',
        value: false,
      }]);
    },
  },
};
</script>

<style lang="scss" scoped>

.payment-button {
  height: 48px;
  width: 100%;
  border-radius: 8px;
  font-weight: 700;
  font-size: 1.125rem;
  display: flex;
  align-items: center;
  justify-content: center;

  img {
    height: 40%;
    object-fit: contain;
  }

  &:not(:last-child) {
    margin-bottom: 8px;
  }

  &:first-child {
    background-color: $NBLUE100;
    color: $WHITE;
  }

  &:nth-child(2) {
    background-color: $SUNSET100;
    color: $WHITE;
  }
  &.cardless {
    background-color: #f1f252;
  }
}
</style>
